import { useRef, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownWideNarrow as SortNewestIcon,
  ArrowUpNarrowWide as SortOldestIcon,
  Mail as MailIcon,
  PanelLeftClose as CollapseListIcon,
  Plus as PlusIcon,
  Search as SearchIcon,
  X as XIcon,
} from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../../components/channel/channel-presentation.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import {
  listFilterButtonCount,
  listFilterChips,
  showListFilterReset,
  type ListFilterChip,
  type MobileFilterSortDraft,
} from "../conversation-list-filters.js";
import { InboxIconButton, InboxQuickButton } from "../InboxIconButton.js";
import { InboxUnreadConversationPill } from "../InboxUnreadConversationPill.js";
import { InboxViewsMenu } from "../InboxViewsMenu.js";
import type { ConversationListViewName } from "@multi-chat/shared";
import { ConversationFilterPopover } from "./ConversationFilterPopover.js";
import { ConversationSearchField } from "./ConversationSearchField.js";
import { MobileFiltersTrigger } from "./MobileFiltersSheet.js";

const SEARCH_ROW_ID = "inbox-list-search";

export function ConversationListHeader({
  isMobile,
  showSubtitle,
  searchOpen,
  onSearchOpenChange,
  searchResetKey,
  committedSearch,
  searching,
  onSearchChange,
  onClearSearch,
  searchInputRef,
  filters,
  onFiltersChange,
  onResetFilters,
  onOpenMobileFilters,
  onOpenLookup,
  onCollapseList,
  collapseButtonRef,
  view,
  onViewChange,
  showMineView,
}: {
  isMobile: boolean;
  showSubtitle: boolean;
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  /** Remounts the search field to drop its local text. */
  searchResetKey: number;
  committedSearch: string | undefined;
  searching: boolean;
  onSearchChange: (search: string | undefined) => void;
  onClearSearch: () => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  filters: MobileFilterSortDraft;
  onFiltersChange: (next: MobileFilterSortDraft) => void;
  /** Clears every filter, and the search too while its field is closed. */
  onResetFilters: () => void;
  onOpenMobileFilters: () => void;
  onOpenLookup: () => void;
  onCollapseList: () => void;
  collapseButtonRef: RefObject<HTMLButtonElement | null>;
  view: ConversationListViewName;
  onViewChange: (view: ConversationListViewName) => void;
  showMineView: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const searchToggleRef = useRef<HTMLButtonElement | null>(null);
  const searchActive = committedSearch != null && committedSearch.trim() !== "";
  const filterCount = listFilterButtonCount(filters);
  const chips = listFilterChips({
    search: committedSearch,
    searchOpen,
    platform: filters.platform,
    contactState: filters.contactState,
    status: filters.status,
  });
  const showReset = showListFilterReset(chips.length, filterCount);
  const oldestFirst = filters.sort === "activity_asc";
  const sortValueLabel = t(oldestFirst ? "inbox.sort.oldestFirst" : "inbox.sort.newestFirst");

  const searchToggleTip = searchOpen
    ? t("inbox.search.closeTip")
    : searchActive
      ? t("inbox.search.activeTip", { query: committedSearch.trim() })
      : t("inbox.search.open");

  function setSearchOpen(open: boolean): void {
    onSearchOpenChange(open);
    requestAnimationFrame(() => {
      if (open) {
        const input = searchInputRef.current;
        input?.focus({ preventScroll: true });
        input?.setSelectionRange(input.value.length, input.value.length);
      } else {
        searchToggleRef.current?.focus({ preventScroll: true });
      }
    });
  }

  function chipLabel(chip: ListFilterChip): string {
    switch (chip.key) {
      case "search":
        return chip.query;
      case "platform":
        return t(`channel.${channelLabelI18nKey(chip.platform)}`);
      case "contact":
        return chip.contactState === "SAVED"
          ? t("inbox.filters.contact.saved")
          : t("inbox.filters.contact.unsaved");
      case "status":
        return t(`inbox.filters.status.${chip.status === "OPEN" ? "open" : "closed"}`);
      default: {
        const _exhaustive: never = chip;
        return _exhaustive;
      }
    }
  }

  function removeChip(chip: ListFilterChip): void {
    switch (chip.key) {
      case "search":
        onClearSearch();
        return;
      case "platform":
        onFiltersChange({ ...filters, platform: undefined });
        return;
      case "contact":
        onFiltersChange({ ...filters, contactState: undefined });
        return;
      case "status":
        onFiltersChange({ ...filters, status: "ALL" });
        return;
      default: {
        const _exhaustive: never = chip;
        return _exhaustive;
      }
    }
  }

  return (
    <header className="shrink-0 border-b border-inbox-line-soft bg-surface px-2.5 pt-3 pb-2">
      <div className="flex min-h-8 items-center gap-1">
        <h1 className="m-0 min-w-0">
          <InboxViewsMenu view={view} onViewChange={onViewChange} showMine={showMineView} />
        </h1>
        <InboxUnreadConversationPill narrow={isMobile} />
        <div className="ms-auto flex shrink-0 items-center gap-1">
          <SimpleTooltip content={searchToggleTip}>
            <InboxIconButton
              ref={searchToggleRef}
              active={searchOpen || searchActive}
              aria-label={t("inbox.search.open")}
              aria-expanded={searchOpen}
              aria-controls={SEARCH_ROW_ID}
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="inbox-search-toggle"
            >
              <SearchIcon size={18} aria-hidden="true" />
              {!searchOpen && searchActive ? (
                <span
                  className="absolute top-1.5 end-1.5 size-[7px] rounded-full bg-primary shadow-[0_0_0_2px_#fff]"
                  aria-hidden="true"
                />
              ) : null}
            </InboxIconButton>
          </SimpleTooltip>
          <SimpleTooltip content={t("inbox.lookup.open")}>
            <InboxIconButton
              aria-label={t("inbox.lookup.open")}
              aria-haspopup="dialog"
              onClick={onOpenLookup}
              data-testid="inbox-contact-lookup"
            >
              <PlusIcon size={18} aria-hidden="true" />
            </InboxIconButton>
          </SimpleTooltip>
        </div>
      </div>
      {showSubtitle ? (
        <p className="m-0 mt-px px-1.5 text-[0.78125rem] text-inbox-muted">{t("inbox.subtitle")}</p>
      ) : null}

      <div
        id={SEARCH_ROW_ID}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-[260ms] ease-inbox motion-reduce:transition-none",
          searchOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
        inert={!searchOpen || undefined}
      >
        <div className="-mx-[3px] min-h-0 overflow-hidden px-[3px]">
          <ConversationSearchField
            key={searchResetKey}
            searching={searching}
            onSearchChange={onSearchChange}
            inputRef={searchInputRef}
            onEscape={() => setSearchOpen(false)}
          />
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-1">
        <div
          role="toolbar"
          aria-label={t("inbox.filters.quickTools")}
          className="flex min-h-[30px] min-w-0 flex-1 items-center gap-0.5"
        >
          <SimpleTooltip content={filters.unread ? t("inbox.filters.unreadOnTip") : t("inbox.filters.unreadOffTip")}>
            <InboxQuickButton
              aria-pressed={filters.unread}
              aria-label={t("inbox.filters.unreadOnly")}
              onClick={() => onFiltersChange({ ...filters, unread: !filters.unread })}
              data-testid="inbox-quick-unread"
            >
              <span className="relative inline-grid place-items-center">
                <MailIcon size={16} aria-hidden="true" />
                <span
                  className="absolute -top-px -end-px size-[5px] rounded-full bg-current ring-[1.5px] ring-white"
                  aria-hidden="true"
                />
              </span>
            </InboxQuickButton>
          </SimpleTooltip>
          <SimpleTooltip content={oldestFirst ? t("inbox.filters.sortOldestTip") : t("inbox.filters.sortNewestTip")}>
            <InboxQuickButton
              aria-pressed={oldestFirst}
              aria-label={t("inbox.filters.sortToggleLabel", { value: sortValueLabel })}
              onClick={() =>
                onFiltersChange({ ...filters, sort: oldestFirst ? "activity_desc" : "activity_asc" })
              }
              data-testid="inbox-quick-sort"
            >
              {oldestFirst ? (
                <SortOldestIcon size={16} aria-hidden="true" />
              ) : (
                <SortNewestIcon size={16} aria-hidden="true" />
              )}
            </InboxQuickButton>
          </SimpleTooltip>
          {isMobile ? (
            <MobileFiltersTrigger
              platform={filters.platform}
              contactState={filters.contactState}
              onClick={onOpenMobileFilters}
            />
          ) : (
            <ConversationFilterPopover value={filters} onChange={onFiltersChange} onReset={onResetFilters} />
          )}
          {chips.length > 0 ? (
            <>
              <span className="mx-1 h-4 w-px shrink-0 bg-inbox-line" aria-hidden="true" />
              <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto py-0.5 [mask-image:linear-gradient(to_right,#000_85%,transparent)] [scrollbar-width:none] rtl:[mask-image:linear-gradient(to_left,#000_85%,transparent)]">
                {chips.map((chip) => {
                  const label = chipLabel(chip);
                  return (
                    <span
                      key={chip.key}
                      className="inline-flex h-6 shrink-0 items-center gap-[5px] rounded-full bg-brand-mint ps-[9px] pe-0.5 text-[0.71875rem] font-[550] whitespace-nowrap text-[#126c56]"
                      data-testid={`inbox-filter-chip-${chip.key}`}
                    >
                      {chip.key === "search" ? <SearchIcon size={12} aria-hidden="true" /> : null}
                      {chip.key === "platform" ? (
                        <ChannelIdentityBadge platformType={chip.platform} size="inline" />
                      ) : null}
                      <bdi className="max-w-[110px] truncate">{label}</bdi>
                      <button
                        type="button"
                        className="grid size-5 place-items-center rounded-full text-[#126c56] hover:bg-[rgb(23_126_103/0.12)] focus-visible:outline-2 focus-visible:outline-primary"
                        aria-label={t("inbox.filters.removeChip", { name: label })}
                        onClick={() => removeChip(chip)}
                      >
                        <XIcon size={12} aria-hidden="true" />
                      </button>
                    </span>
                  );
                })}
                {showReset ? (
                  <button
                    type="button"
                    className="shrink-0 rounded-md px-1 py-0.5 text-[0.75rem] font-[650] text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
                    onClick={onResetFilters}
                  >
                    {t("inbox.filters.reset")}
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
        {!isMobile ? (
          <SimpleTooltip content={t("inbox.list.collapse")}>
            <InboxQuickButton
              ref={collapseButtonRef}
              aria-label={t("inbox.list.collapse")}
              onClick={onCollapseList}
            >
              <CollapseListIcon size={16} className="rtl:-scale-x-100" aria-hidden="true" />
            </InboxQuickButton>
          </SimpleTooltip>
        ) : null}
      </div>
    </header>
  );
}
